import React from 'react';

import { mui } from '../libs';

/**
 * Props for the EmptyMessage component
 */
type EmptyMessageProps = {
  /** Content to display in the empty message */
  children: React.ReactNode;
  /** Optional title text to display */
  title?: string;

  /** Optional icon element to display above the content */
  icon?: React.ReactNode;
  /** Optional action element (like a button) to display below content */
  action?: React.ReactNode;
  /** Whether to hide the border and box shadow styling */
  hideBorder?: boolean;
};

/**
 * A component for displaying an empty state message with optional title, icon and action
 *
 * @param title - Optional title text
 * @param icon - Optional icon element
 * @param action - Optional action element
 * @param hideBorder - Whether to hide border styling
 * @param children - Content to display
 * @returns The rendered empty message component
 */
const EmptyMessage = ({ title, icon, action, hideBorder = false, children }: EmptyMessageProps) => {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <mui.core.Box my={4}>
      <mui.core.Card
        variant="outlined"
        style={hideBorder ? { padding: '3rem', boxShadow: 'none', border: 0 } : {}}
        sx={{
          borderRadius: 4,
          background: hideBorder ? 'rgba(0,0,0,.001)' : '#fff',
          p: 5,
          textAlign: 'center',
          opacity: mounted ? 1 : 0,
          transition: 'opacity 0.5s ease-in-out',
          '&:hover': {
            background: 'none',
          }
        }}
      >
        {icon && <mui.core.IconButton sx={{ pointerEvents: 'none' }}>{icon}</mui.core.IconButton>}
        {title && (
          <mui.core.Typography variant="h4" mt={1} mb={3}>
            {title}
          </mui.core.Typography>
        )}

        <mui.core.Typography variant="body2" color="textSecondary">
          {children}
        </mui.core.Typography>

        {action && <mui.core.Box mt={4}>{action}</mui.core.Box>}
      </mui.core.Card>
    </mui.core.Box>
  );
};

export default EmptyMessage;
